/**
 * Marketing attribution capture — first-touch UTM + referral params.
 *
 * Marketing links (e.g. the WAF Free spotlight) arrive with query params like
 * `?utm_campaign=waf_free&utm_source=fb&ref=ABC`. These must be captured on the
 * very first page load, before the router redirects/strips the query, so
 * `captureAttribution()` runs at module load in `main.tsx` (next to initSentry).
 *
 * Persisted to sessionStorage (one marketing visit == one tab session) with
 * first-touch semantics: the first campaign link of the session wins; a later
 * param-less navigation never clobbers it. Downstream consumers (register form,
 * contact form, affiliate click tracking, `utm_campaign=waf_free` routing) read
 * via `getAttribution()`. Field names mirror the backend `AffiliateClick` shape
 * (see `@/api/affiliate`) so a future click POST is a straight pass-through.
 */

const STORAGE_KEY = 'partnercfg.attribution';

export interface Attribution {
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  utm_content?: string;
  utm_term?: string;
  /** Referral / promo code from ?ref= | ?referral_code= | ?promo= | ?promo_code=. */
  promo_code?: string;
  /**
   * Affiliate referral code from ?ref= | ?referral_code=. Distinct from promo_code: this maps to
   * an affiliate's `/ref/{code}` link and is forwarded on signup (see getAffiliateRefCode). The
   * cookie `_gcl_aff` is the primary source; this URL-captured value is only a fallback.
   */
  ref_code?: string;
  /** Full URL at first capture. */
  landing_url?: string;
  /** document.referrer at first capture. */
  referer_url?: string;
  /** ISO timestamp of first capture. */
  captured_at?: string;
}

const UTM_KEYS = ['utm_source', 'utm_campaign', 'utm_medium', 'utm_content', 'utm_term'] as const;
// Accept the common referral-code aliases marketing may put on a link.
const REFERRAL_KEYS = ['ref', 'referral_code', 'promo', 'promo_code'] as const;
// Affiliate-only aliases (an affiliate's /ref/{code} link), captured separately into ref_code.
const AFFILIATE_KEYS = ['ref', 'referral_code'] as const;

// The JS-readable affiliate cookie DataCentral's /ref/:code handler drops on the *.vnetwork.vn
// landing host. Readable here because the console is served on partner.vnetwork.vn (same domain).
const AFFILIATE_COOKIE = '_gcl_aff';

/** Read a cookie value by name (URI-decoded), or undefined. */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const escaped = name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function read(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

function write(a: Attribution): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(a));
  } catch {
    // Best-effort — ignore storage failures (private mode / quota).
  }
}

/**
 * Capture UTM + referral params from the current URL, once per session
 * (first-touch). No-op when a capture already exists or the URL carries no
 * attribution params. Safe to call at module load. Returns the effective
 * attribution, or null when nothing is attributed.
 */
export function captureAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null;

  const existing = read();
  if (existing) return existing; // first-touch — keep the session's first campaign.

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return null;
  }

  const captured: Attribution = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) captured[k] = v;
  }
  for (const k of REFERRAL_KEYS) {
    const v = params.get(k);
    if (v) {
      captured.promo_code = v;
      break;
    }
  }
  for (const k of AFFILIATE_KEYS) {
    const v = params.get(k);
    if (v) {
      captured.ref_code = v;
      break;
    }
  }

  // Nothing to attribute — leave storage empty so a later real campaign link
  // in this session can still win first-touch.
  if (Object.keys(captured).length === 0) return null;

  captured.landing_url = window.location.href;
  captured.referer_url = document.referrer || undefined;
  captured.captured_at = new Date().toISOString();
  write(captured);
  return captured;
}

/** The captured attribution for this session, or null. */
export function getAttribution(): Attribution | null {
  return read();
}

/** The captured `utm_campaign`, trimmed + lowercased, or undefined. */
export function getCampaign(): string | undefined {
  return read()?.utm_campaign?.trim().toLowerCase() || undefined;
}

/** The captured referral / promo code, or undefined. */
export function getReferralCode(): string | undefined {
  return read()?.promo_code?.trim() || undefined;
}

/**
 * The affiliate referral code to attribute a signup to. Source of truth is the first-party
 * `_gcl_aff` cookie DataCentral's /ref/:code handler drops on the *.vnetwork.vn landing host
 * (persists ~30 days, across tabs/sessions — so it is read live here, not from sessionStorage).
 * Falls back to a `?ref=`/`?referral_code=` captured this session. Empty → undefined.
 */
export function getAffiliateRefCode(): string | undefined {
  const cookie = readCookie(AFFILIATE_COOKIE)?.trim();
  if (cookie) return cookie;
  return read()?.ref_code?.trim() || undefined;
}

/**
 * The affiliate click visitor id (DataCentral's /ref/:code sets it alongside _gcl_aff). Forwarded
 * on signup so the backend can mark the exact click converted — which populates the affiliate's
 * "registrations from link" tab and total_conversions. Undefined when there was no ref click.
 */
export function getAffiliateVisitorId(): string | undefined {
  return readCookie('visitor_id')?.trim() || undefined;
}
